import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = ["General Sourcing", "Electronics", "Apparel & Textiles", "Industrial & Machinery", "Packaging", "Consumer Goods", "Raw Materials"];

export default function NewRequest() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", category: "General Sourcing", product_details: "", quantity: "", target_price: "", notes: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("File must be under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, data: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, attachment_name: file?.name || null, attachment_data: file?.data || null };
      const { data } = await api.post("/requests", payload);
      toast.success("Request submitted! Our team will review it shortly.");
      navigate(`/portal/requests/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none transition";

  return (
    <div className="max-w-2xl">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-coral mb-6 transition" data-testid="back-to-requests">
        <ArrowLeft size={15} /> Back to requests
      </Link>
      <h1 className="font-serif text-4xl font-bold tracking-tight">New sourcing request</h1>
      <p className="text-zinc-500 mt-2 mb-8">Give us the details and we'll source it for you.</p>

      <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-8 space-y-5" data-testid="new-request-form">
        <div>
          <label className="text-sm font-semibold">Request title *</label>
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 5,000 stainless steel water bottles" className={`${inp} mt-1.5`} data-testid="req-title" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="text-sm font-semibold">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inp} mt-1.5`} data-testid="req-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold">Quantity *</label>
            <input required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 5,000 units" className={`${inp} mt-1.5`} data-testid="req-quantity" />
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold">Product details *</label>
          <textarea required rows={4} value={form.product_details} onChange={(e) => setForm({ ...form, product_details: e.target.value })} placeholder="Specs, materials, dimensions, certifications needed..." className={`${inp} mt-1.5`} data-testid="req-details" />
        </div>
        <div>
          <label className="text-sm font-semibold">Target price (optional)</label>
          <input value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} placeholder="e.g. $4.50 / unit" className={`${inp} mt-1.5`} data-testid="req-target-price" />
        </div>
        <div>
          <label className="text-sm font-semibold">Additional notes</label>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inp} mt-1.5`} data-testid="req-notes" />
        </div>
        <div>
          <label className="text-sm font-semibold">Attachment (optional)</label>
          {file ? (
            <div className="mt-1.5 flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-sm px-4 py-3 text-sm" data-testid="req-file-preview">
              <span className="truncate">{file.name}</span>
              <button type="button" onClick={() => setFile(null)} className="text-zinc-400 hover:text-coral"><X size={16} /></button>
            </div>
          ) : (
            <label className="mt-1.5 flex items-center gap-3 bg-zinc-50 border border-dashed border-zinc-300 rounded-sm px-4 py-4 text-sm text-zinc-500 cursor-pointer hover:border-coral transition" data-testid="req-file-label">
              <Upload size={18} /> Upload spec sheet, drawing, or reference (max 4MB)
              <input type="file" className="hidden" onChange={onFile} data-testid="req-file-input" />
            </label>
          )}
        </div>
        <button disabled={loading} className="w-full bg-coral hover:bg-coral-hover text-white font-semibold py-3.5 rounded-sm transition disabled:opacity-60" data-testid="submit-request">
          {loading ? "Submitting..." : "Submit Request"}
        </button>
      </form>
    </div>
  );
}
